<template>
  <!-- A storage gate kiosk as it looks right now: the kiosk's own screen
       (1024 × 600), read-only, scaled down like the signage preview. -->
  <figure class="flex h-full flex-col">
    <div
      class="relative overflow-hidden rounded-lg border bg-white"
      :style="{ height: `${height}px`, width: `${width}px` }"
    >
      <iframe
        v-if="src"
        :src="src"
        class="origin-top-left border-0"
        :style="{
          width: `${KIOSK_WIDTH}px`,
          height: `${KIOSK_HEIGHT}px`,
          transform: `scale(${scale})`,
        }"
        :title="`${lane.name} kiosk`"
        loading="lazy"
      />
      <p
        v-else-if="failed"
        class="text-background-fg/70 flex h-full items-center justify-center p-4 text-sm"
      >
        The kiosk view didn't load.
      </p>
      <BaseContainerLoading v-else />
    </div>
    <figcaption class="mt-1.5 flex items-center gap-1.5 text-xs">
      <span
        class="inline-block h-2 w-2 rounded-full"
        :class="isOnline ? 'bg-green' : 'bg-red'"
        aria-hidden="true"
      />
      <span class="text-background-fg/70">
        {{ lane.name }} kiosk
        <template v-if="isOnline">online</template>
        <template v-else-if="lane.lastSeenAt">
          offline, last seen {{ lastSeen }}
        </template>
        <template v-else>hasn't checked in yet</template>
      </span>
    </figcaption>
  </figure>
</template>

<script setup lang="ts">
import { getKioskViewUrl } from "@/services/storage/getKioskViewUrl";
import { type StorageLane } from "@/services/storage/storage";

const props = withDefaults(
  defineProps<{ lane: StorageLane; height?: number }>(),
  { height: 288 },
);

const KIOSK_WIDTH = 1024;
const KIOSK_HEIGHT = 600;
// A kiosk checks in every 30 s; two missed heartbeats is offline.
const OFFLINE_AFTER_MS = 2 * 60 * 1000;

const scale = computed(() => props.height / KIOSK_HEIGHT);
const width = computed(() => Math.round(KIOSK_WIDTH * scale.value));

const src = ref<string>();
const failed = ref(false);
watch(
  () => props.lane.id,
  async (laneId) => {
    src.value = undefined;
    failed.value = false;
    try {
      const url = await getKioskViewUrl(laneId);
      if (props.lane.id === laneId) src.value = url;
    } catch {
      failed.value = true;
    }
  },
  { immediate: true },
);

const now = useNow({ interval: 15 * 1000 });
const isOnline = computed(
  () =>
    !!props.lane.lastSeenAt &&
    now.value.getTime() - DateFns.parseISO(props.lane.lastSeenAt).getTime() <
      OFFLINE_AFTER_MS,
);
const lastSeen = computed(() =>
  props.lane.lastSeenAt
    ? DateFns.formatDistanceToNow(DateFns.parseISO(props.lane.lastSeenAt), {
        addSuffix: true,
      })
    : "",
);
</script>
